import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';

export type ObjectiveSwitchTarget = 'own_brand' | 'other_channels';

const COPY_PREFIX: Record<ObjectiveSwitchTarget, 'account.switchToOwnContent' | 'account.switchToClippingChannels'> = {
  own_brand: 'account.switchToOwnContent',
  other_channels: 'account.switchToClippingChannels',
};

interface ObjectiveSwitchConfirmDialogProps {
  to: ObjectiveSwitchTarget | null;
  clipperMonthlyClipLimit?: number | 'unlimited';
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ObjectiveSwitchConfirmDialog({
  to,
  clipperMonthlyClipLimit,
  loading,
  onCancel,
  onConfirm,
}: ObjectiveSwitchConfirmDialogProps) {
  const { t } = useTranslation();
  const prefix = to ? COPY_PREFIX[to] : COPY_PREFIX.other_channels;

  return (
    <Dialog
      open={to !== null}
      onOpenChange={(open) => {
        if (!open && !loading) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md" hideCloseButton={loading}>
        <DialogHeader>
          <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <section>
            <h3 className="font-semibold text-foreground">{t(`${prefix}.getHeading`)}</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>{t(`${prefix}.getHome`)}</li>
              <li>{t(`${prefix}.getWorkflow`)}</li>
              {to === 'own_brand' && (
                <li>{t(`${prefix}.getUnlimitedClips`)}</li>
              )}
              {to === 'other_channels' && (
                <>
                  <li>{t(`${prefix}.getAcervo`)}</li>
                  <li>{t(`${prefix}.getUnlimitedCurations`)}</li>
                  {clipperMonthlyClipLimit === 'unlimited' ? (
                    <li>{t(`${prefix}.getUnlimitedClips`)}</li>
                  ) : clipperMonthlyClipLimit ? (
                    <li>{t(`${prefix}.getClips`, { count: clipperMonthlyClipLimit })}</li>
                  ) : null}
                </>
              )}
            </ul>
          </section>
          <section>
            <h3 className="font-semibold text-foreground">{t(`${prefix}.loseHeading`)}</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>{t(`${prefix}.loseHome`)}</li>
              <li>{t(`${prefix}.loseMinutes`)}</li>
              <li>{t(`${prefix}.loseNoMigrate`)}</li>
              {to === 'own_brand' && (
                <li>{t(`${prefix}.loseAcervo`)}</li>
              )}
            </ul>
          </section>
          <DialogDescription>
            {t(`${prefix}.keep`)}
          </DialogDescription>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            {t(`${prefix}.cancel`)}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading}
          >
            {t(`${prefix}.confirm`)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
